import { useEffect, useState, type FormEvent } from 'react'
import { useAccountEnv, isApiError } from '../client'
import type { CustomerProfile, ProfilePatch, Plate, PaymentMethod, SavedCard, Visit } from '../types'
import {
  Row, SecLbl, Field, ErrorNote, BackButton,
  mapsUrl, initialsOf, todayDateValue, defaultRemoveDate, formatShortDate, formatCardBrand, maskPhone,
  formatPhoneDisplay,
  URGENT_MIN, HELP_URL, TERMS_URL, PRIVACY_URL, SUPPORT_E164, SUPPORT_PRETTY,
} from '../primitives'
import { CarSvg, CardSvg, ChatSvg, PhoneSvg, HelpSvg, ShieldSvg, DocSvg, BellSvg, LogoutSvg, WidgetMark } from '../icons'
import { DatePicker } from '@parkify/ui'
import { OtpInput } from '../OtpInput'

// Lenient email shape — mirrors the server's check (local@domain.tld, no
// whitespace). We don't verify deliverability: signing in with this address
// still requires the emailed one-time code, so a typo can't be used to log in.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export function ProfileHeader({
  profile, onSaved,
}: {
  profile: CustomerProfile
  onSaved: (next: Omit<CustomerProfile, 'visits'>) => void
}) {
  const { client } = useAccountEnv()
  const [editing, setEditing] = useState(false)
  const [nameInput, setNameInput] = useState(profile.name ?? '')
  const [emailInput, setEmailInput] = useState(profile.email ?? '')
  const [changingPhone, setChangingPhone] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startEdit = () => {
    setNameInput(profile.name ?? '')
    setEmailInput(profile.email ?? '')
    setChangingPhone(false)
    setError(null)
    setEditing(true)
  }

  // Name + email save here. The phone is changed through its own SMS-verified
  // flow (PhoneChangeForm) — it never rides this patch.
  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)

    const patch: ProfilePatch = { name: nameInput.trim() || null }
    const email = emailInput.trim().toLowerCase()
    if (email) {
      if (!EMAIL_RE.test(email)) {
        setError('Enter a valid email address.')
        return
      }
      patch.email = email
    }

    setSubmitting(true)
    try {
      const updated = await client.patchProfile(patch)
      onSaved(updated)
      setEditing(false)
    } catch (e) {
      // Surfaces the server's 409 ("That email is already in use…") verbatim.
      setError(isApiError(e) ? e.message : 'Could not save changes.')
    } finally {
      setSubmitting(false)
    }
  }

  if (editing) {
    return (
      <div className="pka-card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 16px' }}>Edit profile</h2>
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Name">
            <input id="profile-name" className="pka-field" type="text" placeholder="Your name"
              maxLength={128} autoComplete="name" autoFocus value={nameInput}
              onChange={e => setNameInput(e.target.value)} disabled={submitting} />
          </Field>
          <Field label="Email">
            <input className="pka-field" type="email" inputMode="email" autoComplete="email"
              placeholder="you@example.com" maxLength={255} value={emailInput}
              onChange={e => setEmailInput(e.target.value)} disabled={submitting} />
          </Field>
          <Field label="Phone number">
            {changingPhone ? (
              <PhoneChangeForm
                onCancel={() => setChangingPhone(false)}
                onChanged={updated => { onSaved(updated); setChangingPhone(false) }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input className="pka-field" type="tel" value={profile.phone ?? ''} readOnly
                  placeholder="No number added"
                  style={{ flex: 1, color: 'var(--color-gray-700)', cursor: 'not-allowed' }} />
                <button type="button" onClick={() => { setChangingPhone(true); setError(null) }} disabled={submitting}
                  style={{
                    flexShrink: 0, background: 'var(--color-white)', border: '1.5px solid var(--color-gray-300)',
                    borderRadius: 9, padding: '9px 14px', color: 'var(--color-brand)', fontWeight: 700, fontSize: 12,
                  }}>{profile.phone ? 'Change' : 'Add'}</button>
              </div>
            )}
            {!changingPhone && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
                Used for SMS receipts and reservation reminders if you opt in below.
                Changing it requires verifying the new number by text.
              </p>
            )}
          </Field>

          {error && <ErrorNote>{error}</ErrorNote>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={() => { setEditing(false); setChangingPhone(false); setError(null) }} disabled={submitting}
              className="pka-btn-text" style={{ fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={submitting} className="pka-btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}>
              {submitting ? <span className="pka-spinner" /> : 'Save'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="pka-card" style={{ marginBottom: 16 }}>
      {profile.is_new && (
        <p style={{
          margin: '0 0 16px', fontSize: 13, color: 'var(--color-gray-700)',
          background: 'var(--color-brand-light)', border: '1px solid var(--color-brand-border)',
          padding: '10px 12px', borderRadius: 'var(--radius-sm)', lineHeight: 1.55,
        }}>
          Welcome! We've just created your Parkify profile. Add a license plate below
          and your sessions and receipts will show up here automatically.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%', background: 'var(--color-brand)',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--color-white)', fontSize: 20, fontWeight: 800,
        }} aria-hidden>{initialsOf(profile.name) ?? <WidgetMark />}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)', marginBottom: 1, wordBreak: 'break-word' }}>
            {profile.name || 'Your account'}
          </div>
          {profile.phone && <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500 }}>{formatPhoneDisplay(profile.phone)}</div>}
        </div>
        <button type="button" onClick={startEdit} style={{
          background: 'var(--color-white)', border: '1.5px solid var(--color-gray-300)',
          borderRadius: 9, padding: '7px 13px', color: 'var(--color-brand)',
          fontWeight: 700, fontSize: 12, flexShrink: 0,
        }}>Edit</button>
      </div>
    </div>
  )
}

/**
 * SMS-verified phone-number change. Rendered INSIDE the Edit-profile <form>, so
 * every control is type="button" (no nested form) and it drives its own two-step
 * flow: enter the new number → we text a code → enter the code → the server
 * persists the change (resetting the SMS opt-in) and re-mints the session, which
 * we hand back to the host via onSessionRefreshed.
 */
function PhoneChangeForm({
  onChanged, onCancel,
}: {
  onChanged: (updated: Omit<CustomerProfile, 'visits'>) => void
  onCancel: () => void
}) {
  const { client, onSessionRefreshed } = useAccountEnv()
  const [step, setStep] = useState<'enter' | 'code'>('enter')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendCode = async () => {
    if (busy) return
    const next = phone.trim()
    if (!next) { setError('Enter a mobile number.'); return }
    setBusy(true); setError(null)
    try {
      await client.startPhoneChange({ phone: next })
      setStep('code')
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not send a code. Try again.')
    } finally { setBusy(false) }
  }

  const verify = async () => {
    if (busy) return
    if (!/^\d{4,10}$/.test(code.trim())) { setError('Enter the code we texted you.'); return }
    setBusy(true); setError(null)
    try {
      const res = await client.verifyPhoneChange({ phone: phone.trim(), code: code.trim() })
      // Persist the re-minted session BEFORE surfacing the update, so a
      // phone-first account's next request authenticates with the new identity.
      onSessionRefreshed?.(res.token, res.csrf)
      onChanged(res.profile)
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not verify that code.')
    } finally { setBusy(false) }
  }

  return (
    <div style={{
      background: 'var(--color-brand-light)', border: '1px solid var(--color-brand-border)',
      borderRadius: 'var(--radius-md)', padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {step === 'enter' ? (
        <>
          <div style={{ fontSize: 12, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
            Enter your new mobile number — we'll text a code to confirm it's yours.
          </div>
          <input className="pka-field" type="tel" autoFocus inputMode="tel" autoComplete="tel"
            placeholder="(555) 123-4567" maxLength={32} value={phone}
            onChange={e => setPhone(e.target.value)} disabled={busy}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void sendCode() } }} />
          {error && <ErrorNote>{error}</ErrorNote>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onCancel} disabled={busy} className="pka-btn-text" style={{ fontSize: 13 }}>Cancel</button>
            <button type="button" onClick={() => void sendCode()} disabled={busy || !phone.trim()}
              className="pka-btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}>
              {busy ? <span className="pka-spinner" /> : 'Text me a code'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
            We texted a 6-digit code to <strong>{formatPhoneDisplay(phone.trim())}</strong>.
          </div>
          {/* The shared segmented input, not a plain field. This flow used to be
              the one 6-digit surface that hand-rolled its own `<input>`, which
              meant it silently missed everything OtpInput had learned — most
              importantly that iOS SMS autofill drops the WHOLE code into the
              box, and the old `maxLength` truncated it. */}
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={() => void verify()}
            disabled={busy}
            error={!!error}
            autoFocus
            aria-label="6-digit verification code"
          />
          <p style={{ margin: 0, fontSize: 11, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
            Changing your number turns SMS notifications off — re-enable them below to text the new number.
          </p>
          {error && <ErrorNote>{error}</ErrorNote>}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button type="button" onClick={() => { setStep('enter'); setCode(''); setError(null) }} disabled={busy}
              className="pka-btn-text" style={{ fontSize: 13 }}>← Back</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onCancel} disabled={busy} className="pka-btn-text" style={{ fontSize: 13 }}>Cancel</button>
              <button type="button" onClick={() => void verify()} disabled={busy || !code.trim()}
                className="pka-btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}>
                {busy ? <span className="pka-spinner" /> : 'Verify & save'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Live-session card. The Visit payload has no expiry timestamp, so when the
 * server gives us both a start and a purchased duration we derive the expiry
 * (real timestamps → display only) and flip to the attention state under
 * URGENT_MIN. When duration is absent we simply omit the countdown rather than
 * fabricate one. "Add time" and "Details" both jump to the History tab, where
 * the (unchanged, money-authoritative) extend flow lives — passing this
 * session's txnId so the parent focuses + expands THIS row instead of landing
 * on a generic, unfocused History tab (a Mitte-sourced visit has no txnId, so
 * those fall back to an unfocused landing, same as before).
 */
export function ActiveSessionCard({ visit, onManage }: { visit: Visit | null; onManage: (txnId: string | null) => void }) {
  const { onFindParking } = useAccountEnv()
  if (!visit) {
    return (
      <button
        type="button"
        onClick={onFindParking}
        style={{
          display: 'block', width: '100%', font: 'inherit', cursor: 'pointer',
          background: 'var(--color-gray-100)', border: '1.5px dashed var(--pk-brand-mid)',
          borderRadius: 'var(--radius-md)', padding: 18, textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)', marginBottom: 3 }}>No active session</div>
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500 }}>
          Scan a code or search a lot to get started.
        </div>
      </button>
    )
  }

  let expiresClock: string | null = null
  let minutesLeft: number | null = null
  // Prefer the authoritative expiry the server now sends for a live session;
  // fall back to started_at + purchased duration for legacy rows that lack it.
  let expiresAt: Date | null = null
  if (visit.expires_at) {
    expiresAt = new Date(visit.expires_at)
  } else if (visit.started_at && visit.duration_minutes != null) {
    expiresAt = new Date(new Date(visit.started_at).getTime() + visit.duration_minutes * 60000)
  }
  if (expiresAt && !Number.isNaN(expiresAt.getTime())) {
    expiresClock = expiresAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    minutesLeft = Math.round((expiresAt.getTime() - Date.now()) / 60000)
  }
  const urgent = minutesLeft != null && minutesLeft > 0 && minutesLeft <= URGENT_MIN
  const zoneName = visit.zone.name || 'your session'
  // Same derivation HistoryRow uses to match focusSession against a row.
  const txnId = visit.id.startsWith('parkify:') ? visit.id.slice('parkify:'.length) : null

  return (
    <div style={{
      background: 'var(--color-brand)', borderRadius: 'var(--radius-md)',
      padding: '15px 17px', color: 'var(--color-white)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8 }}>
        {urgent ? (
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
            background: 'var(--color-error-bg)', color: 'var(--color-white)',
            padding: '3px 9px', borderRadius: 999,
          }}>Expiring soon</span>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.9 }}>
            Active session
          </span>
        )}
        <span style={{
          fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,0.2)',
          padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
        }}>{zoneName}</span>
      </div>
      {expiresClock ? (
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.3, marginBottom: 2 }}>Expires {expiresClock}</div>
      ) : (
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3, marginBottom: 2 }}>In progress</div>
      )}
      <div style={{ fontSize: 12, opacity: 0.9, fontWeight: 500, marginBottom: 8 }}>
        {visit.plate ? `Plate ${visit.plate.toUpperCase()}` : 'Session active'}
        {minutesLeft != null && minutesLeft > 0 ? ` · ${minutesLeft} min left` : ''}
      </div>
      {visit.zone.address && (
        <a href={mapsUrl(visit.zone.address)} target="_blank" rel="noopener noreferrer" style={{
          display: 'block', color: 'var(--color-white)', opacity: 0.85, fontWeight: 700,
          fontSize: 12, textDecoration: 'underline', marginBottom: 13,
        }}>Get directions</a>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => onManage(txnId)} style={{
          flex: 1, background: 'var(--color-white)', borderRadius: 10, padding: '10px 0',
          color: 'var(--color-brand)', fontWeight: 700, fontSize: 13,
        }}>+ Add time</button>
        <button type="button" onClick={() => onManage(txnId)} style={{
          flex: 1, background: 'rgba(255,255,255,0.18)', borderRadius: 10, padding: '10px 0',
          color: 'var(--color-white)', fontWeight: 700, fontSize: 13,
        }}>Details</button>
      </div>
    </div>
  )
}

// ── vehicles (plates) ───────────────────────────────────────────────────────

export function PlatesSection({
  plates, onPlatesChange,
}: {
  plates: Plate[]
  onPlatesChange: (next: Plate[]) => void
}) {
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Primary plate first, matching the demo's default-first ordering.
  const ordered = [...plates].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))

  return (
    <>
      <SecLbl action={adding ? undefined : 'Add'} onAction={() => { setAdding(true); setError(null) }}>Vehicles</SecLbl>
      {plates.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, padding: '12px 0' }}>
          No vehicles yet — add the plate you park with so we can match your sessions and receipts.
        </div>
      )}
      {ordered.map((p, i) => (
        <PlateRow key={p.id} plate={p} last={!adding && i === ordered.length - 1} onRemoved={onPlatesChange} />
      ))}
      {error && <div style={{ marginTop: 10 }}><ErrorNote>{error}</ErrorNote></div>}
      {adding && (
        <div style={{ marginTop: 12 }}>
          <AddPlateForm
            existingCount={plates.length}
            onCancel={() => { setAdding(false); setError(null) }}
            onSaved={next => { onPlatesChange(next); setAdding(false); setError(null) }}
            onError={setError}
          />
        </div>
      )}
    </>
  )
}

function PlateRow({
  plate, last, onRemoved,
}: {
  plate: Plate
  last?: boolean
  onRemoved: (next: Plate[]) => void
}) {
  const { client } = useAccountEnv()
  const [removing, setRemoving] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleRemove = async () => {
    if (removing) return
    setRemoving(true)
    setError(null)
    try {
      const { plates } = await client.removePlate(plate.id)
      onRemoved(plates)
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not remove plate.')
      setRemoving(false)
      setConfirming(false)
    }
  }

  return (
    <div className={`pka-row${last ? ' pka-row--last' : ''}`} style={{ cursor: 'default' }}>
      <span className="pka-row__icon"><CarSvg /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: 'var(--color-black)' }}>
          {plate.plate}
          {plate.is_primary && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.3, color: 'var(--color-success)',
              background: 'var(--color-success-bg)', padding: '2px 7px', borderRadius: 999,
            }}>DEFAULT</span>
          )}
        </span>
        {plate.expires_at ? (
          // Temporary plate — show the removal date (wraps so it never clips).
          <span style={{ display: 'block', fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, marginTop: 1, lineHeight: 1.35 }}>
            Temporary · removes {formatShortDate(plate.expires_at)}
            {plate.nickname ? ` · ${plate.nickname}` : ''}
          </span>
        ) : plate.nickname ? (
          <span style={{ display: 'block', fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, marginTop: 1 }}>
            {plate.nickname}
          </span>
        ) : null}
        {error && <span style={{ display: 'block', fontSize: 12, color: 'var(--color-error)', marginTop: 4 }}>{error}</span>}
      </span>
      {!confirming ? (
        <button type="button" onClick={() => { setConfirming(true); setError(null) }}
          style={{ fontSize: 12, color: 'var(--color-gray-700)', padding: '4px 8px', flexShrink: 0 }}>Remove</button>
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button type="button" onClick={() => setConfirming(false)} disabled={removing}
            style={{ fontSize: 12, color: 'var(--color-gray-700)', padding: '4px 8px' }}>Cancel</button>
          <button type="button" onClick={handleRemove} disabled={removing} style={{
            fontSize: 12, fontWeight: 700, color: 'var(--color-white)', background: 'var(--color-error)',
            padding: '4px 12px', borderRadius: 'var(--radius-full)',
          }}>{removing ? 'Removing…' : 'Confirm'}</button>
        </span>
      )}
    </div>
  )
}

function AddPlateForm({
  existingCount, onSaved, onCancel, onError,
}: {
  existingCount: number
  onSaved: (plates: Plate[]) => void
  onCancel: () => void
  onError: (msg: string) => void
}) {
  const { client } = useAccountEnv()
  const [plateInput, setPlateInput] = useState('')
  const [nicknameInput, setNicknameInput] = useState('')
  const [makePrimary, setMakePrimary] = useState(existingCount === 0)
  const [kind, setKind] = useState<'saved' | 'temp'>('saved')
  const [removeDate, setRemoveDate] = useState(defaultRemoveDate())
  const [submitting, setSubmitting] = useState(false)

  const isTemp = kind === 'temp'

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    const plate = plateInput.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (plate.length < 2) {
      onError('Plate must be at least 2 alphanumeric characters.')
      return
    }
    // Temporary plates carry a removal date, interpreted as end-of-day UTC so
    // the plate stays usable through the chosen day. A temp plate is never the
    // default, so the primary flag is suppressed for it.
    let expires_at: string | undefined
    if (isTemp) {
      if (!removeDate) { onError('Pick a removal date for the temporary plate.'); return }
      const parsed = new Date(`${removeDate}T23:59:59Z`)
      if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now()) {
        onError('The removal date must be in the future.'); return
      }
      expires_at = parsed.toISOString()
    }
    setSubmitting(true)
    try {
      const res = await client.addPlate({
        plate,
        nickname: nicknameInput.trim() || undefined,
        is_primary: isTemp ? false : makePrimary,
        expires_at,
      })
      onSaved(res.plates)
    } catch (e) {
      onError(isApiError(e) ? e.message : 'Could not add plate.')
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} style={{
      background: 'var(--color-brand-light)', border: '1px solid var(--color-brand-border)',
      borderRadius: 'var(--radius-md)', padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <Field label="License plate">
        <input id="new-plate" className="pka-field" type="text" required autoFocus
          autoCapitalize="characters" autoCorrect="off" spellCheck={false} maxLength={16}
          placeholder="ABC1234" value={plateInput} onChange={e => setPlateInput(e.target.value)}
          disabled={submitting}
          style={{ fontFamily: "'Courier New', monospace", fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }} />
      </Field>
      <Field label="Nickname (optional)">
        <input id="new-nickname" className="pka-field" type="text" placeholder="Daily driver"
          maxLength={64} value={nicknameInput} onChange={e => setNicknameInput(e.target.value)} disabled={submitting} />
      </Field>
      <Field label="Save as">
        <div style={{ display: 'flex', gap: 6, background: 'var(--color-gray-200)', borderRadius: 'var(--radius-md)', padding: 4 }}>
          {([['saved', 'Permanent'], ['temp', 'Temporary']] as const).map(([id, label]) => (
            <button key={id} type="button" disabled={submitting} onClick={() => setKind(id)}
              style={{
                flex: 1, borderRadius: 'var(--radius-sm)', padding: '8px 0', fontSize: 13, fontWeight: 700,
                background: kind === id ? 'var(--color-white)' : 'transparent',
                color: kind === id ? 'var(--color-brand)' : 'var(--color-gray-700)',
                boxShadow: kind === id ? 'var(--shadow-sm)' : 'none',
              }}>{label}</button>
          ))}
        </div>
      </Field>
      {isTemp && (
        <Field label="Removes on">
          <DatePicker value={removeDate} min={todayDateValue()} onChange={date => setRemoveDate(date ?? '')} disabled={submitting} />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
            We'll remove this plate automatically on that date.
          </p>
        </Field>
      )}
      {existingCount > 0 && !isTemp && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-gray-700)' }}>
          <input type="checkbox" checked={makePrimary} onChange={e => setMakePrimary(e.target.checked)} disabled={submitting} />
          Make this my default plate
        </label>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={submitting} className="pka-btn-text" style={{ fontSize: 13 }}>Cancel</button>
        <button type="submit" disabled={submitting || plateInput.replace(/[^A-Za-z0-9]/g, '').length < 2}
          className="pka-btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}>
          {submitting ? <span className="pka-spinner" /> : 'Add plate'}
        </button>
      </div>
    </form>
  )
}

export function PaymentMethodSection() {
  const { client, billingPortalEnabled, renderAddCard } = useAccountEnv()
  const [loading, setLoading] = useState(false)          // billing-portal open in flight
  const [error, setError] = useState<string | null>(null)
  const [card, setCard] = useState<PaymentMethod | null>(null)
  const [cardLoaded, setCardLoaded] = useState(false)
  // Every saved card. Cards accumulate on their own — checkout saves the card it
  // charged — so a driver can hold several without ever having "added" one. The
  // list is optional on the client interface: a host that predates it simply
  // keeps the single-card view rather than breaking.
  const [cards, setCards] = useState<SavedCard[] | null>(null)
  // Which card is being promoted, so only that row shows a pending state.
  const [settingDefault, setSettingDefault] = useState<string | null>(null)
  // Two-step remove: first tap arms, second confirms. Removing a card is not
  // undoable from here, and it sits inches from "Make default".
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  // Inline rename: which card is being named, and the draft text.
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draftLabel, setDraftLabel] = useState('')
  const [savingLabel, setSavingLabel] = useState(false)
  const [addingCard, setAddingCard] = useState(false)

  // Fetch the saved card's brand + last4 for display. Read-only and
  // non-blocking — on any failure we just leave the generic "Saved card".
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        if (client.listPaymentMethods) {
          const res = await client.listPaymentMethods()
          if (!cancelled) setCards(res.methods)
        }
      } catch {
        // Non-blocking: fall back to the single-card view below.
      }
      try {
        const pm = await client.fetchPaymentMethod()
        if (!cancelled) setCard(pm)
      } catch { /* display-only — keep the generic label */ }
      finally { if (!cancelled) setCardLoaded(true) }
    })()
    return () => { cancelled = true }
  }, [])

  const open = async () => {
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const { url } = await client.createBillingSession()
      // Same-tab navigation — the hosted portal returns the customer to
      // account.parkify.com via the return_url we set server-side, and the
      // back button works if they bail mid-flow.
      window.location.href = url
    } catch (e) {
      const msg = isApiError(e) ? e.message : 'Could not open card management right now.'
      // eslint-disable-next-line no-console
      console.warn('[parkify-portal] billing-session failed:', msg)
      setError(msg)
      setLoading(false)
    }
  }

  // Prefer the LIST when we have one. The legacy single-card fetch happens once
  // on mount, so after a remove it still describes the card that was deleted —
  // the singular view rendered the removed card as if it survived. The list is
  // the state we actually mutate, so it is the honest source once loaded.
  const singleFromList = cards && cards.length === 1 ? cards[0]! : null
  const shown: { brand: string | null; last4: string | null } | null =
    singleFromList ?? (cards && cards.length === 0 ? null : card)
  const hasCard = !!shown?.last4
  const title = hasCard ? `${formatCardBrand(shown!.brand)} •••• ${shown!.last4}` : 'Saved card'
  const sub = hasCard
    ? 'Used for parking sessions and auto-pay'
    : cardLoaded ? 'No card on file yet' : 'Used for parking sessions and auto-pay'

  // A wallet card reports where it came from; a typed one doesn't. Shown because
  // "which of these two Visas is which" is otherwise unanswerable from 4 digits.
  const walletLabel = (w: string | null): string | null =>
    w === 'apple_pay' ? 'Added via Apple Pay'
      : w === 'google_pay' ? 'Added via Google Pay'
        : w ? 'Added via wallet' : null

  const expLabel = (c: SavedCard): string | null =>
    c.expMonth && c.expYear ? `Expires ${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}` : null

  const makeDefault = async (id: string) => {
    if (!client.setDefaultPaymentMethod || settingDefault) return
    setSettingDefault(id)
    setError(null)
    try {
      await client.setDefaultPaymentMethod(id)
      // Re-flag locally rather than refetching: the server already told us it
      // took, and a round trip here would blank the list mid-interaction.
      setCards((prev) => prev?.map((c) => ({ ...c, isDefault: c.id === id })) ?? prev)
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not update your default card.')
    } finally {
      setSettingDefault(null)
    }
  }

  const removeCard = async (id: string) => {
    if (!client.removePaymentMethod || removing) return
    setRemoving(id)
    setError(null)
    try {
      const res = await client.removePaymentMethod(id)
      setCards((prev) => (prev ?? [])
        .filter((c) => c.id !== id)
        .map((c) => ({ ...c, isDefault: c.id === res.defaultId })))
      setConfirmRemove(null)
    } catch (e) {
      // The 409 ("this card pays for a monthly subscription") is the server's
      // own wording and is the whole point — surface it, don't flatten it.
      setError(isApiError(e) ? e.message : 'Could not remove that card.')
      setConfirmRemove(null)
    } finally {
      setRemoving(null)
    }
  }

  const refreshCards = async () => {
    if (!client.listPaymentMethods) return
    try {
      const res = await client.listPaymentMethods()
      setCards(res.methods)
    } catch {
      // Non-blocking: the card is added either way; the list catches up on the
      // next load rather than throwing an error over a successful save.
    }
  }

  // Shown above the list and in the empty state. The slot decides what the form
  // is; this only decides when it's open.
  const addCardBlock = renderAddCard ? (
    addingCard ? (
      <div style={{ marginTop: 10 }}>
        {renderAddCard({
          onAdded: () => { setAddingCard(false); void refreshCards() },
          onCancel: () => setAddingCard(false),
        })}
      </div>
    ) : (
      <button type="button" onClick={() => { setAddingCard(true); setError(null) }}
        className="pka-btn-text"
        style={{ fontSize: 12.5, fontWeight: 700, marginTop: 10 }}>
        + Add a card
      </button>
    )
  ) : null

  const saveLabel = async (id: string) => {
    if (!client.setCardLabel || savingLabel) return
    setSavingLabel(true)
    setError(null)
    try {
      const res = await client.setCardLabel(id, draftLabel)
      setCards((prev) => prev?.map((c) => (c.id === id ? { ...c, label: res.label } : c)) ?? prev)
      setRenaming(null)
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not save that name.')
    } finally {
      setSavingLabel(false)
    }
  }

  // Multi-card view. Only when the host supports the list AND there is more than
  // one — a single card reads better in the existing summary row than as a list
  // of one, and every host keeps working if the endpoint is unavailable.
  if (cards && cards.length > 1) {
    return (
      <>
        <SecLbl>Payment methods</SecLbl>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cards.map((c) => (
            <div key={c.id} style={{
              background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px',
              // Wrap, and give the details a floor to shrink to: with three text
              // actions (Rename / Make default / Remove) beside them, the card
              // details otherwise squeezed "Amex •••• 3005 · Expires 11/27" into
              // a five-line wrap at 390px. Now the ACTIONS drop to their own line
              // instead, which also stops the row degrading each time this arc
              // adds another verb.
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <div style={{
                width: 34, height: 34, background: 'var(--color-white)', border: '1px solid var(--color-gray-300)',
                borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><CardSvg /></div>
              {renaming === c.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <input className="pka-field" autoFocus maxLength={24}
                    placeholder="Personal" value={draftLabel} disabled={savingLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void saveLabel(c.id) }
                      if (e.key === 'Escape') setRenaming(null)
                    }}
                    style={{ flex: 1, minWidth: 0, fontSize: 13, padding: '6px 10px' }} />
                  <button type="button" onClick={() => setRenaming(null)} disabled={savingLabel}
                    className="pka-btn-text" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Cancel</button>
                  <button type="button" onClick={() => void saveLabel(c.id)} disabled={savingLabel}
                    className="pka-btn-text"
                    style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {savingLabel ? 'Saving…' : 'Save'}
                  </button>
                </div>
              ) : (
              <>
              <div style={{ minWidth: 150, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)' }}>
                    {c.label || `${formatCardBrand(c.brand)} •••• ${c.last4}`}
                  </span>
                  {c.isDefault && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase',
                      color: 'var(--color-brand)', background: 'var(--color-brand-light)',
                      borderRadius: 4, padding: '2px 6px',
                    }}>Default</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-gray-600)', fontWeight: 500 }}>
                  {/* A nickname replaces the brand line, so the brand + last4 move
                      here — a card you can't identify is worse than an unnamed one. */}
                  {[
                    c.label ? `${formatCardBrand(c.brand)} •••• ${c.last4}` : null,
                    walletLabel(c.wallet),
                    expLabel(c),
                  ].filter(Boolean).join(' · ') || 'Saved card'}
                </div>
              </div>
              {/* Only on the cards that aren't already default — a "Make default"
                  on the default card is a no-op dressed as a control. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, marginLeft: 'auto' }}>
                {client.setCardLabel && confirmRemove !== c.id && (
                  <button type="button"
                    onClick={() => { setRenaming(c.id); setDraftLabel(c.label ?? ''); setError(null) }}
                    disabled={!!removing || !!settingDefault}
                    className="pka-btn-text"
                    style={{ fontSize: 12, color: 'var(--color-gray-600)', whiteSpace: 'nowrap' }}>
                    {c.label ? 'Rename' : 'Name'}
                  </button>
                )}
                {!c.isDefault && client.setDefaultPaymentMethod && confirmRemove !== c.id && (
                  <button type="button" onClick={() => void makeDefault(c.id)}
                    disabled={!!settingDefault || !!removing}
                    className="pka-btn-text"
                    style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {settingDefault === c.id ? 'Saving…' : 'Make default'}
                  </button>
                )}
                {client.removePaymentMethod && (
                  confirmRemove === c.id ? (
                    <>
                      <button type="button" onClick={() => setConfirmRemove(null)} disabled={!!removing}
                        className="pka-btn-text" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        Cancel
                      </button>
                      <button type="button" onClick={() => void removeCard(c.id)} disabled={!!removing}
                        className="pka-btn-text"
                        style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-error)', whiteSpace: 'nowrap' }}>
                        {removing === c.id ? 'Removing…' : 'Confirm'}
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => { setConfirmRemove(c.id); setError(null) }}
                      disabled={!!removing || !!settingDefault}
                      className="pka-btn-text"
                      style={{ fontSize: 12, color: 'var(--color-gray-600)', whiteSpace: 'nowrap' }}>
                      Remove
                    </button>
                  )
                )}
              </div>
              </>
              )}
            </div>
          ))}
        </div>
        {addCardBlock}
        {billingPortalEnabled && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={open} disabled={loading}
              style={{ color: 'var(--color-brand)', fontWeight: 700, fontSize: 12.5 }}>
              {loading ? 'Opening…' : 'Manage cards'}
            </button>
          </div>
        )}
        {error && <div style={{ marginTop: 10 }}><ErrorNote>{error}</ErrorNote></div>}
        <div style={{ fontSize: 11.5, color: 'var(--color-gray-600)', fontWeight: 500, lineHeight: 1.55, marginTop: 10 }}>
          Adding time to a session always charges the card that started it, not
          whichever card is default here.
        </div>
      </>
    )
  }

  return (
    <>
      <SecLbl>Payment method</SecLbl>
      <div style={{
        background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)',
        borderRadius: 'var(--radius-md)', padding: '14px 15px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{
              width: 34, height: 34, background: 'var(--color-white)', border: '1px solid var(--color-gray-300)',
              borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><CardSvg /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)' }}>{title}</div>
              <div style={{ fontSize: 11, color: 'var(--color-gray-600)', fontWeight: 500 }}>{sub}</div>
            </div>
          </div>
          {billingPortalEnabled && (
            <button type="button" onClick={open} disabled={loading}
              style={{ color: 'var(--color-brand)', fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {loading ? 'Opening…' : hasCard ? 'Manage' : 'Add card'}
            </button>
          )}
        </div>
        {error && <div style={{ marginBottom: 10 }}><ErrorNote>{error}</ErrorNote></div>}
        <div style={{ fontSize: 11.5, color: 'var(--color-gray-600)', fontWeight: 500, lineHeight: 1.55 }}>
          Your card is stored and processed securely by our payment provider — we never
          see or store your full card number.
        </div>
        {addCardBlock}
      </div>
    </>
  )
}

export function SupportSection({ onOpenAvery }: { onOpenAvery: () => void }) {
  return (
    <>
      <SecLbl>Support</SecLbl>
      <Row icon={<ChatSvg />} title="Chat with Avery" sub="Get help right here" onClick={onOpenAvery} />
      <Row icon={<PhoneSvg />} title="Call us" sub={SUPPORT_PRETTY} href={`tel:${SUPPORT_E164}`} />
      <Row icon={<HelpSvg />} title="Help Center" sub="Guides & FAQs" href={HELP_URL} last />
    </>
  )
}

export function SettingsSection({
  onOpenPrivacy, onOpenComms, onSignOut,
}: {
  onOpenPrivacy: () => void
  onOpenComms: () => void
  onSignOut: () => void
}) {
  // Privacy + Communication preferences open in-app sub-screens (back button);
  // Terms stays an external link to the legal doc. Sign out is the last row —
  // per Austin (Slack, 2026-07-17) it belongs here, in the Account tab, not as
  // its own affordance in the host chrome (portal header / consumer's account
  // pane) — so both hosts stop rendering their own and call this instead.
  return (
    <>
      <SecLbl>Settings</SecLbl>
      <Row icon={<ShieldSvg />} title="Privacy" sub="What we collect and why" onClick={onOpenPrivacy} />
      <Row icon={<BellSvg />} title="Communication preferences" sub="Email, SMS & auto-pay" onClick={onOpenComms} />
      <Row icon={<DocSvg />} title="Terms of Service" sub="parkify.com/articles/terms" href={TERMS_URL} />
      <Row icon={<LogoutSvg />} title="Sign out" onClick={onSignOut} danger last />
    </>
  )
}

// Privacy sub-screen — a plain-language summary of what Parkify collects and why
// (the demo's "collect & why" view), replacing the bare external link. The full
// legal Privacy Policy stays one tap away at the bottom. NB: we say "our payment
// provider" rather than naming the processor — no vendor names in customer copy.
const PRIVACY_ITEMS: Array<[string, string]> = [
  ['Mobile number', 'Used to create and verify your account and send the parking texts you opt into. Never sold or shared.'],
  ['License plates', 'Stored so we can pre-fill your plate at checkout and match your sessions and receipts. Remove them anytime.'],
  ['Session history', 'Kept so you can view and email your receipts. Visible only to you and Parkify support.'],
  ['Location', "Never stored. Used only while you're choosing a zone, to show parking near you."],
  ['Payment info', 'Never stored by Parkify — your card is stored and processed securely by our payment provider.'],
]

export function PrivacyScreen({ onBack }: { onBack: () => void }) {
  return (
    <div className="pka-fadein" style={{ marginTop: 16 }}>
      <BackButton onBack={onBack} />
      <div className="pka-card">
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px', letterSpacing: -0.2 }}>Privacy</h1>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
          Here's exactly what Parkify collects and why.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {PRIVACY_ITEMS.map(([title, desc]) => (
            <div key={title}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)', marginBottom: 2 }}>{title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', lineHeight: 1.55 }}>{desc}</div>
            </div>
          ))}
        </div>
        <p style={{ margin: '20px 0 0', fontSize: 12, color: 'var(--color-gray-600)', lineHeight: 1.55 }}>
          Read the full{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand)', fontWeight: 600 }}>
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  )
}

// Communication-preferences sub-screen — the (previously always-visible)
// PreferencesCard, now reached from its own Settings row so the account tab
// stays a clean list. Content + TCPA disclosures are carried over verbatim.
export function CommPrefsScreen({
  profile, onSaved, onBack,
}: {
  profile: CustomerProfile
  onSaved: (next: Omit<CustomerProfile, 'visits'>) => void
  onBack: () => void
}) {
  return (
    <div className="pka-fadein" style={{ marginTop: 16 }}>
      <BackButton onBack={onBack} />
      <PreferencesCard profile={profile} onSaved={onSaved} />
    </div>
  )
}

export function PreferencesCard({
  profile, onSaved,
}: {
  profile: CustomerProfile
  onSaved: (next: Omit<CustomerProfile, 'visits'>) => void
}) {
  const { client } = useAccountEnv()
  const [savingField, setSavingField] = useState<keyof ProfilePatch | null>(null)
  const [error, setError] = useState<{ field: keyof ProfilePatch; msg: string } | null>(null)

  const toggle = async (
    field: 'opt_in_email' | 'opt_in_sms' | 'opt_in_auto_park',
    nextValue: boolean,
  ) => {
    if (savingField) return
    setSavingField(field)
    setError(null)
    const optimistic: Omit<CustomerProfile, 'visits'> = { ...profile, [field]: nextValue }
    onSaved(optimistic)
    try {
      const updated = await client.patchProfile({ [field]: nextValue })
      onSaved(updated)
    } catch (e) {
      onSaved({ ...profile, [field]: !nextValue })
      setError({ field, msg: isApiError(e) ? e.message : 'Could not save change.' })
    } finally {
      setSavingField(null)
    }
  }

  return (
    <div className="pka-card">
      <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 6px', letterSpacing: -0.2 }}>Communication preferences</h2>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--color-gray-600)' }}>Changes save automatically.</p>
      <PreferenceToggle
        label="Email notifications"
        description="Receipts and reservation reminders sent to your email."
        checked={profile.opt_in_email}
        saving={savingField === 'opt_in_email'}
        error={error?.field === 'opt_in_email' ? error.msg : null}
        onChange={v => toggle('opt_in_email', v)}
      />
      <SmsSubscriptionRow
        profile={profile}
        saving={savingField === 'opt_in_sms'}
        error={error?.field === 'opt_in_sms' ? error.msg : null}
        onConfirm={() => toggle('opt_in_sms', true)}
        onOptOut={() => toggle('opt_in_sms', false)}
      />
      <PreferenceToggle
        label="Auto-pay enrollment"
        description="Skip paying at the kiosk — we auto-bill your card when you exit."
        checked={profile.opt_in_auto_park}
        saving={savingField === 'opt_in_auto_park'}
        error={error?.field === 'opt_in_auto_park' ? error.msg : null}
        onChange={v => toggle('opt_in_auto_park', v)}
        last
      />
    </div>
  )
}

/**
 * SMS subscription / opt-out row (TCPA-compliant). Copy + structure are the
 * proof linked in the SMS provider's "opt-in policy proof" field — keep the
 * disclosures verbatim so the hosted screenshot matches production.
 */
function SmsSubscriptionRow({
  profile, saving, error, onConfirm, onOptOut,
}: {
  profile: CustomerProfile
  saving: boolean
  error: string | null
  onConfirm: () => Promise<void> | void
  onOptOut: () => Promise<void> | void
}) {
  const [consented, setConsented] = useState(false)

  if (!profile.phone) {
    return (
      <PreferenceToggle
        label="SMS notifications"
        description="Add a phone number above to enable SMS notifications."
        checked={false}
        disabled
        onChange={() => { /* no-op */ }}
      />
    )
  }

  if (profile.opt_in_sms) {
    return (
      <PreferenceToggle
        label="SMS notifications"
        description={`Active — texted to ${maskPhone(profile.phone)} when sessions expire, validations are applied, or time is added. Reply STOP to opt out at any time.`}
        checked
        saving={saving}
        error={error}
        onChange={() => onOptOut()}
      />
    )
  }

  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid var(--color-gray-200)' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-black)' }}>SMS notifications</div>
      <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 2, marginBottom: 14, lineHeight: 1.5 }}>
        Get parking confirmations, expiration reminders, and account notifications via text.
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (saving || !consented) return
          await onConfirm()
        }}
        style={{
          background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)',
          borderRadius: 'var(--radius-sm)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div>
          <label htmlFor="sms-mobile" style={{
            display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 1.2, color: 'var(--color-gray-700)', marginBottom: 6,
          }}>Mobile phone number</label>
          <input id="sms-mobile" className="pka-field" type="tel" value={profile.phone} readOnly
            style={{ background: 'var(--color-white)', color: 'var(--color-gray-700)', cursor: 'not-allowed' }} />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-gray-600)' }}>
            To change, edit your phone number in the Account section above.
          </p>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--color-black)', lineHeight: 1.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={consented} onChange={e => setConsented(e.target.checked)} disabled={saving}
            style={{ marginTop: 3, flexShrink: 0 }} />
          <span>
            Yes, I would like to receive automated text messages from Parkify about my parking
            sessions, expiration reminders, enforcement notices, and account updates. I understand
            I will receive up to 6 messages per month.
          </span>
        </label>

        <div style={{ fontSize: 12, color: 'var(--color-gray-700)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Message Frequency:</strong> You will receive up to 6 messages per month, varying with your parking activity.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Standard Rates:</strong> Message and data rates may apply depending on your mobile phone service plan.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Help &amp; Stop:</strong> Reply HELP for help or STOP to cancel at any time.
          </p>
          <p style={{ margin: 0, color: 'var(--color-gray-600)', fontSize: 11 }}>
            By providing your phone number and checking the box above, you agree to receive
            automated text messages from Parkify. Consent is not required to make a purchase.
          </p>
        </div>

        <div style={{ fontSize: 11 }}>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand)' }}>Terms of Service</a>
          <span style={{ color: 'var(--color-gray-500)', margin: '0 6px' }}>|</span>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand)' }}>Privacy Policy</a>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <button type="submit" disabled={!consented || saving} className="pka-btn-primary"
          style={{ fontSize: 14, padding: '12px 24px', alignSelf: 'flex-start' }}>
          {saving ? <span className="pka-spinner" /> : 'Yes, sign me up!'}
        </button>
      </form>
    </div>
  )
}

function PreferenceToggle({
  label, description, checked, disabled, saving, error, onChange, last,
}: {
  label: string
  description?: string
  checked: boolean
  disabled?: boolean
  saving?: boolean
  error?: string | null
  onChange: (v: boolean) => void
  last?: boolean
}) {
  const handleClick = () => {
    if (disabled || saving) return
    onChange(!checked)
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 16, padding: '12px 0',
      borderBottom: last ? 'none' : '1px solid var(--color-gray-200)',
      opacity: disabled ? 0.6 : 1,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-black)' }}>{label}</div>
        {description && (
          <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 2, lineHeight: 1.5 }}>{description}</div>
        )}
        {error && <div style={{ fontSize: 12, color: 'var(--color-error)', marginTop: 4 }}>{error}</div>}
      </div>
      <button
        type="button" role="switch" aria-checked={checked} aria-label={label}
        disabled={disabled || saving} onClick={handleClick}
        style={{
          flexShrink: 0, width: 44, height: 24, borderRadius: 999, padding: 0,
          background: checked ? 'var(--color-brand)' : 'var(--color-gray-300)',
          position: 'relative', cursor: disabled || saving ? 'not-allowed' : 'pointer',
          transition: 'background 160ms ease',
        }}
      >
        <span aria-hidden style={{
          position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20,
          borderRadius: '50%', background: 'var(--color-white)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 160ms ease',
        }} />
      </button>
    </div>
  )
}
